"use client";

import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Info, Wrench, ShieldCheck, Settings2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "50px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const commonProblems = [{
  title: "Vertragsgegenstand & Umfang",
  description: "Unsere präzise definierten Leistungsbeschreibungen bilden die verbindliche Grundlage für alle industriellen Fertigungsprozesse und technischen Dienstleistungen.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1594803/img/earthworks_image.png"
}, {
  title: "Preise & Zahlungskonditionen",
  description: "Klare Strukturen bei Vergütung, Zahlungszielen und Nebenkosten sichern eine transparente Geschäftsbeziehung ohne unvorhergesehene finanzielle Abweichungen.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1594803/img/earthworks_image.png"
}];
const repairSteps = [{
  icon: Settings2,
  title: "Geltungsbereich",
  description: "Diese Bedingungen gelten für sämtliche Lieferungen und Leistungen im Rahmen unserer geschäftlichen Tätigkeit, sofern nicht schriftlich Abweichungen vereinbart wurden."
}, {
  icon: Wrench,
  title: "Vertragsschluss",
  description: "Ein rechtsverbindlicher Vertrag kommt durch unsere schriftliche Bestätigung oder durch den Beginn der technischen Auftragsausführung unter diesen Bedingungen zustande."
}, {
  icon: ShieldCheck,
  title: "Haftung & Gewährleistung",
  description: "Wir haften für die technische Präzision unserer Leistungen im Rahmen der gesetzlichen Bestimmungen und garantieren die Einhaltung industrieller Qualitätsstandards."
}];
const inspectedParts = ["Widerrufsrecht für gewerbliche Kunden", "Eigentumsvorbehalt bis zur vollständigen Zahlung", "Lieferfristen und Verzugsschadenregelungen", "Gerichtsstand und anwendbares Recht", "Datenschutzkonformität nach aktuellen Richtlinien", "Salvatorische Klausel zur Vertragssicherung"];
export default function RepairDetails() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: problemsRef,
    isVisible: areProblemsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: processRef,
    isVisible: isProcessVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: partsRef,
    isVisible: arePartsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="152566" id="agb-überblick" className="bg-muted py-24 overflow-hidden">
      <div className="container mx-auto px-4 max-w-5xl">
        {}
        <div ref={headerRef} className={`transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Alert className="bg-primary/10 border-primary/20 shadow-sm mb-20 py-6 px-6 md:px-8 rounded-none backdrop-blur-sm">
            <Info className="size-6 text-primary mt-1" />
            <AlertTitle className="text-secondary font-sans font-extrabold text-xl mb-3 tracking-tighter">
              Rechtlicher Hinweis zur Gültigkeit
            </AlertTitle>
            <AlertDescription className="text-foreground/90 font-sans font-medium text-lg leading-relaxed">
              Bitte beachten Sie: Unsere Allgemeinen Geschäftsbedingungen sind speziell auf industrielle Präzisionsleistungen und technische Projekte zugeschnitten. Sie finden keine Anwendung auf automatisierte Privatkunden-Systeme oder Standard-Handelswaren ohne Servicekomponente.
            </AlertDescription>
          </Alert>
        </div>

        {}
        <div ref={problemsRef} className={`mb-24 transition-all duration-1000 delay-100 transform ${areProblemsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-secondary mb-4 font-sans tracking-tighter">
              Allgemeine Geschäftsbedingungen
            </h2>
            <div className="h-1 w-24 bg-primary rounded-none"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {commonProblems.map((problem, i) => <Card key={i} data-index={i} className="border-border shadow-sm overflow-hidden group bg-card backdrop-blur-sm transition-all duration-300 hover:shadow-md rounded-none">
                <div className="overflow-hidden h-64 relative">
                  <div className="absolute inset-0 bg-secondary/10 mix-blend-overlay z-10 transition-opacity duration-500 group-hover:opacity-0"></div>
                  <img src={problem.image} alt={problem.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-sans font-extrabold text-secondary tracking-tight">
                    {problem.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-sans font-medium text-lg text-muted-foreground leading-relaxed">
                    {problem.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {}
        <div ref={processRef} className={`mb-24 transition-all duration-1000 delay-200 transform ${isProcessVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-secondary mb-4 font-sans tracking-tighter">
              Unsere Vertragskonditionen
            </h2>
            <div className="h-1 w-24 bg-primary rounded-none"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {repairSteps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative">
                  {i !== repairSteps.length - 1 && <div className="hidden md:block absolute top-8 left-[60%] w-full h-[2px] bg-border -z-10"></div>}
                  <div className="bg-card border border-border shadow-sm rounded-none p-8 h-full flex flex-col items-start relative z-10">
                    <div className="bg-primary/10 p-4 rounded-none mb-6 text-primary">
                      <Icon className="size-8" />
                    </div>
                    <h3 className="text-xl font-extrabold text-secondary mb-4 font-sans tracking-tight">
                      {step.title}
                    </h3>
                    <p className="font-sans font-medium text-lg text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={partsRef} className={`transition-all duration-1000 delay-300 transform ${arePartsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-secondary mb-4 font-sans tracking-tighter">
              Rechtliche Eckpunkte
            </h2>
            <div className="h-1 w-24 bg-primary rounded-none"></div>
          </div>

          <Card className="border-border shadow-sm bg-card/40 backdrop-blur-md rounded-none">
            <CardContent className="p-8 md:p-12">
              <p className="font-sans font-medium text-lg text-muted-foreground leading-relaxed mb-8 max-w-3xl">
                Ein stabiles rechtliches Fundament ist die Voraussetzung für technische Exzellenz. In unseren Bestimmungen finden Sie alle relevanten Details zu Haftung, Fristen und Datenschutz, um eine reibungslose Projektabwicklung zu garantieren.
              </p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {inspectedParts.map((part, i) => <div key={i} data-index={i} className="flex items-center gap-4 p-3 rounded-none hover:bg-primary/5 transition-colors">
                    <CheckCircle2 className="size-6 text-primary shrink-0" />
                    <span className="font-sans text-lg text-secondary font-bold tracking-tight">
                      {part}
                    </span>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}